import React, { useState, useEffect } from "react";
import { StorageService } from "./services/storage.js";
import { TopBar } from "./components/common/TopBar.js";
import { Navbar } from "./components/common/Navbar.js";
import { Footer } from "./components/common/Footer.js";
import { HomePage } from "./components/public/HomePage.js";
import { AboutPage } from "./components/public/AboutPage.js";
import { CurriculumPage } from "./components/public/CurriculumPage.js";
import { BlogPage } from "./components/public/BlogPage.js";
import { ContactPage } from "./components/public/ContactPage.js";
import { AdminLogin } from "./components/admin/AdminLogin.js";
import { AdminLayout } from "./components/admin/AdminLayout.js";
import { MessageCircle } from "lucide-react";
export default function App() {
  const [currentTab, setCurrentTab] = useState("home");
  const [activeBlogSlug, setActiveBlogSlug] = useState(null);
  const [settings, setSettings] = useState(() => StorageService.getSettings());
  const [pages, setPages] = useState(() => StorageService.getPages());
  const [posts, setPosts] = useState(() => StorageService.getPosts());
  const [curriculum, setCurriculum] = useState(() => StorageService.getCurriculum());
  const [submissions, setSubmissions] = useState(() => StorageService.getSubmissions());
  const [users, setUsers] = useState(() => StorageService.getUsers());
  const [lang, setLang] = useState(() => StorageService.getLanguage());
  const [currentUser, setCurrentUser] = useState(() => StorageService.getCurrentUser());
  useEffect(() => {
    async function fetchBackendData() {
      try {
        const [settingsRes, usersRes, pagesRes, postsRes, curriculumRes, subsRes] = await Promise.all([
          fetch("/api/settings").then((r) => r.json()).catch(() => null),
          fetch("/api/users").then((r) => r.json()).catch(() => []),
          fetch("/api/pages").then((r) => r.json()).catch(() => []),
          fetch("/api/posts").then((r) => r.json()).catch(() => []),
          fetch("/api/curriculum").then((r) => r.json()).catch(() => []),
          fetch("/api/submissions").then((r) => r.json()).catch(() => [])
        ]);
        if (settingsRes && typeof settingsRes === "object" && !settingsRes.error) {
          StorageService.setMemorySettings(settingsRes);
        }
        if (Array.isArray(usersRes)) {
          StorageService.setMemoryUsers(usersRes);
        }
        if (Array.isArray(pagesRes)) {
          StorageService.setMemoryPages(pagesRes);
        }
        if (Array.isArray(postsRes)) {
          StorageService.setMemoryPosts(postsRes);
        }
        if (Array.isArray(curriculumRes)) {
          StorageService.setMemoryCurriculum(curriculumRes);
        }
        if (Array.isArray(subsRes)) {
          StorageService.setMemorySubmissions(subsRes);
        }
        handleRefreshData();
      } catch (e) {
        console.warn("Failed to sync with backend MySQL API:", e);
      }
    }
    fetchBackendData();
    const handleImagesReady = () => {
      handleRefreshData();
    };
    window.addEventListener("bmm-images-ready", handleImagesReady);
    return () => {
      window.removeEventListener("bmm-images-ready", handleImagesReady);
    };
  }, []);
  const handleNavigate = (tab, itemSlug) => {
    setCurrentTab(tab);
    if (tab === "blog" && itemSlug) {
      setActiveBlogSlug(itemSlug);
    } else {
      setActiveBlogSlug(null);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleLangChange = (newLang) => {
    setLang(newLang);
    StorageService.setLanguage(newLang);
  };
  const handleRefreshData = () => {
    setSettings(StorageService.getSettings());
    setPages(StorageService.getPages());
    setPosts(StorageService.getPosts());
    setCurriculum(StorageService.getCurriculum());
    setSubmissions(StorageService.getSubmissions());
    setUsers(StorageService.getUsers());
  };
  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    handleRefreshData();
  };
  const handleLogout = () => {
    StorageService.logout();
    setCurrentUser(null);
    handleRefreshData();
  };
  const handleStatusChange = (id, newStatus) => {
    StorageService.updateSubmissionStatus(id, newStatus);
    setSubmissions(StorageService.getSubmissions());
  };
  if (currentTab === "admin") {
    if (!currentUser) {
      return <AdminLogin
        settings={settings}
        onLoginSuccess={handleLoginSuccess}
        onBackToSite={() => handleNavigate("home")}
      />;
    }
    return <AdminLayout
      currentUser={currentUser}
      onLogout={handleLogout}
      onBackToSite={() => handleNavigate("home")}
      submissions={submissions}
      posts={posts}
      pages={pages}
      curriculum={curriculum}
      settings={settings}
      users={users}
      onRefreshData={handleRefreshData}
      onStatusChange={handleStatusChange}
    />;
  }
  return <div className={`min-h-screen flex flex-col bg-stone-50 ${lang === "bn" ? "font-bengali" : "font-sans"}`}>
      {
    /* Top Header Bar */
  }
      <TopBar
    settings={settings}
    lang={lang}
    onLangChange={handleLangChange}
  />

      {
    /* Main Navbar */
  }
      <Navbar
    currentTab={currentTab}
    onNavigate={handleNavigate}
    lang={lang}
    currentUser={currentUser}
    settings={settings}
  />

      {
    /* Public Pages Viewport */
  }
      <main className="flex-1">
        {currentTab === "home" && <HomePage
    settings={settings}
    posts={posts}
    curriculum={curriculum}
    lang={lang}
    onNavigate={handleNavigate}
    pageData={pages.find((p) => p.slug === "home")}
  />}

        {currentTab === "about" && <AboutPage
    settings={settings}
    lang={lang}
    onNavigate={handleNavigate}
    pageData={pages.find((p) => p.slug === "about")}
  />}

        {currentTab === "curriculum" && <CurriculumPage
    curriculum={curriculum}
    settings={settings}
    lang={lang}
    onNavigate={handleNavigate}
    pageData={pages.find((p) => p.slug === "curriculum")}
  />}

        {currentTab === "blog" && <BlogPage
    posts={posts}
    settings={settings}
    lang={lang}
    activePostSlug={activeBlogSlug}
    onClearActivePost={() => setActiveBlogSlug(null)}
    pageData={pages.find((p) => p.slug === "blog")}
  />}

        {currentTab === "contact" && <ContactPage
    settings={settings}
    lang={lang}
    pageData={pages.find((p) => p.slug === "contact")}
  />}
      </main>

      {
    /* Global Footer */
  }
      <Footer
    settings={settings}
    lang={lang}
    onNavigate={handleNavigate}
  />

      {
    /* Floating Action Buttons */
  }
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 pointer-events-auto">
        {
    /* Floating WhatsApp Quick Link (matching mobile screenshot green circle button) */
  }
        <a
    href={`https://wa.me/${(settings?.whatsapp || "8801712345678").replace(/[^0-9]/g, "")}`}
    target="_blank"
    rel="noopener noreferrer"
    className="w-13 h-13 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 border-2 border-white"
    title="Direct WhatsApp"
  >
          <MessageCircle className="w-7 h-7" />
        </a>
      </div>
    </div>;
}
